import { createSlice } from "@reduxjs/toolkit";
import { deleteProduct, getProducts } from "apis/resnapApi";
import { doDeleteProject, getArticleId, getSavedProjects } from "apis/xserverapi";
import { UserAgent } from "application/savedProjects/projectConstants";
import { mapToObject, mapToPages } from "common/mergeInNewPage";
import { RequestState } from "common/RequestState";
import { handleError } from "services/errorService";
import { oidc } from "services/oidcService";

export const pageSize = 10;

const sortProjects = items =>
  items.sort((a, b) => (new Date(a.ModificationDate) < new Date(b.ModificationDate) ? 1 : -1));

export const projectsSlice = createSlice({
  name: "projects",
  initialState: {
    _requestState: RequestState.NotRequested
  },
  reducers: {
    requested: state => {
      state._requestState = RequestState.InProgress;
    },
    received: (state, action) => {
      state.items = mapToObject(
        action.payload.items,
        item => item.Id,
        item => (item.UserAgent === UserAgent.RESNAP ? RequestState.Finished : RequestState.NotRequested)
      );
      state.total = action.payload.items.length;
      state.pages = mapToPages(sortProjects(action.payload.items), item => item.Id, pageSize);
      state.pageSize = pageSize;
      state._requestState = RequestState.Finished;
    },
    failed: state => {
      state._requestState = RequestState.Failed;
    },

    requestedArticleId: (state, action) => {
      state.items[action.payload.id]._requestState = RequestState.InProgress;
    },
    receivedArticleId: (state, action) => {
      const { id, articleId } = action.payload;
      state.items[id]._requestState = RequestState.Finished;
      state.items[id].ArticleId = articleId;
    },
    failedArticleId: (state, action) => {
      state.items[action.payload.id]._requestState = RequestState.Failed;
    },

    requestedDelete: (state, action) => {
      state.items[action.payload.id]._requestState = RequestState.InProgress;
    },
    receivedDelete: (state, action) => {
      delete state.items[action.payload.id];
      state.total = state.total - 1;
      state.pages = mapToPages(Object.values(state.items), item => item.Id, pageSize);
    },
    failedDelete: (state, action) => {
      state.items[action.payload.id]._requestState = RequestState.Failed;
    }
  }
});

export const requestSavedProjects = () => async dispatch => {
  try {
    dispatch(projectsSlice.actions.requested());
    const accountId = await oidc.getCurrentUserAccountId();
    const albelli = await getSavedProjects({ includeToken: true }, accountId);
    const resnap = await getProducts({ includeToken: true }, "bonusprint.co.uk");
    dispatch(projectsSlice.actions.received({ items: albelli.concat(resnap) }));
  } catch (e) {
    dispatch(projectsSlice.actions.failed());
    handleError(e);
  }
};

export const requestArticleId = id => async dispatch => {
  try {
    dispatch(projectsSlice.actions.requestedArticleId({ id }));
    const articleId = await getArticleId({ includeToken: true }, id);
    dispatch(projectsSlice.actions.receivedArticleId({ id, articleId }));
  } catch (e) {
    dispatch(projectsSlice.actions.failedArticleId({ id }));
    handleError(e);
  }
};

export const requestDeleteProject = project => async dispatch => {
  const id = project.Id;
  try {
    dispatch(projectsSlice.actions.requestedDelete({ id }));
    if (project.UserAgent === UserAgent.RESNAP) {
      await deleteProduct({ includeToken: true }, id);
    } else {
      await doDeleteProject({ includeToken: true }, id);
    }
    dispatch(projectsSlice.actions.receivedDelete({ id }));
  } catch (e) {
    dispatch(projectsSlice.actions.failedDelete({ id }));
    handleError(e);
  }
};
